// WebRTC Peer 连接管理 — RTCPeerConnection + RTCDataChannel 包装
// 来源：浏览器原生 RTCPeerConnection（替换 LocalSend 的 Rust webrtc-rs 实现）
//
// 设计：
// - 主动方创建 offer + 数据通道；被动方接收 offer + setRemoteDescription
// - ICE 候选内联在 SDP（非 trickle，匹配 LocalSend 行为）
// - 数据通道名 'data'，二进制类型 arraybuffer

import {
  BUFFER_HIGH_WATERMARK,
  BUFFER_LOW_WATERMARK,
  CHUNK_SIZE,
  type DataChannelMessage,
  type FileDto,
  type TransferStatus,
} from '@kuaichuan/shared';
import type { SignalingClient } from '../signaling/client';
import { encodeSdp, decodeSdp } from '../signaling/sdp';
import { stringifyDataChannelMessage, parseDataChannelMessage } from './protocol';

export interface WebRTCPeerHandlers {
  onStateChange?: (state: TransferStatus) => void;
  onDataChannelOpen?: () => void;
  onDataChannelClose?: () => void;
  onFileList?: (files: FileDto[]) => void; // 接收方收到文件清单
  onFileProgress?: (fileId: string, receivedBytes: number, totalBytes: number) => void;
  onFileReceived?: (fileId: string, blob: Blob, fileName: string) => void;
  onFileAck?: (fileId: string, success: boolean, error?: string) => void;
  onDecline?: () => void;
  onAllDone?: () => void;
  onCancel?: (reason?: string) => void;
  onError?: (err: Error) => void;
}

export class WebRTCPeer {
  private pc: RTCPeerConnection | null = null;
  private dc: RTCDataChannel | null = null;
  private sessionId: string;
  private isInitiator: boolean;
  private signaling: SignalingClient;
  private targetPeerId: string;
  private handlers: WebRTCPeerHandlers;
  private stunServers: string[];

  // 接收方状态
  private incomingFileBuffers = new Map<string, ArrayBuffer[]>();
  private incomingFileMeta = new Map<string, FileDto>();

  // 发送方状态
  private pendingFiles: FileDto[] = [];
  private currentFileIndex = 0;
  private selectedIds: string[] = [];

  constructor(opts: {
    signaling: SignalingClient;
    targetPeerId: string;
    isInitiator: boolean;
    stunServers: string[];
    handlers: WebRTCPeerHandlers;
  }) {
    this.signaling = opts.signaling;
    this.targetPeerId = opts.targetPeerId;
    this.isInitiator = opts.isInitiator;
    this.stunServers = opts.stunServers;
    this.handlers = opts.handlers;
    this.sessionId = crypto.randomUUID();
  }

  async init(): Promise<void> {
    this.handlers.onStateChange?.('connecting');
    this.pc = new RTCPeerConnection({
      iceServers: this.stunServers.map((urls) => ({ urls })),
    });

    this.pc.addEventListener('connectionstatechange', () => {
      const state = this.pc?.connectionState;
      if (state === 'connected') {
        // 不在这里改状态，等数据通道 open 后再切 connected
      } else if (state === 'failed' || state === 'disconnected' || state === 'closed') {
        this.handlers.onError?.(new Error(`Peer connection ${state}`));
        this.handlers.onStateChange?.('error');
      }
    });

    if (this.isInitiator) {
      // 主动方创建数据通道
      this.dc = this.pc.createDataChannel('data', {
        ordered: true,
      });
      this.setupDataChannel(this.dc);
      await this.createAndSendOffer();
    } else {
      // 被动方等待对方的 datachannel
      this.pc.addEventListener('datachannel', (event) => {
        this.dc = event.channel;
        this.setupDataChannel(this.dc);
      });
    }
  }

  // 被动方收到 OFFER 后调用
  async handleOffer(sessionId: string, sdpEncoded: string): Promise<void> {
    if (!this.pc) await this.init();
    if (sessionId) this.sessionId = sessionId;
    if (!this.pc) return;

    const sdp = decodeSdp(sdpEncoded);
    await this.pc.setRemoteDescription({ type: 'offer', sdp });
    const answer = await this.pc.createAnswer();
    await this.pc.setLocalDescription(answer);
    // 等待 ICE 收集完成（非 trickle）
    await this.waitForIceComplete();
    const answerSdp = this.pc.localDescription!.sdp;
    this.signaling.sendAnswer(this.sessionId, this.targetPeerId, encodeSdp(answerSdp));
    this.handlers.onStateChange?.('sdpExchanged');
  }

  // 主动方收到 ANSWER 后调用
  async handleAnswer(sdpEncoded: string): Promise<void> {
    if (!this.pc) return;
    const sdp = decodeSdp(sdpEncoded);
    await this.pc.setRemoteDescription({ type: 'answer', sdp });
    this.handlers.onStateChange?.('sdpExchanged');
  }

  private async createAndSendOffer(): Promise<void> {
    if (!this.pc) return;
    const offer = await this.pc.createOffer();
    await this.pc.setLocalDescription(offer);
    await this.waitForIceComplete();
    const offerSdp = this.pc.localDescription!.sdp;
    this.signaling.sendOffer(this.sessionId, this.targetPeerId, encodeSdp(offerSdp));
  }

  private waitForIceComplete(): Promise<void> {
    return new Promise((resolve) => {
      if (!this.pc) return resolve();
      if (this.pc.iceGatheringState === 'complete') return resolve();
      const check = () => {
        if (this.pc?.iceGatheringState === 'complete') {
          this.pc.removeEventListener('icegatheringstatechange', check);
          resolve();
        }
      };
      this.pc.addEventListener('icegatheringstatechange', check);
      // 兜底超时（1.5s）— 防止某些情况下 ICE 卡住
      setTimeout(() => {
        this.pc?.removeEventListener('icegatheringstatechange', check);
        resolve();
      }, 1500);
    });
  }

  private setupDataChannel(dc: RTCDataChannel) {
    dc.binaryType = 'arraybuffer';
    dc.bufferedAmountLowThreshold = BUFFER_LOW_WATERMARK;

    dc.addEventListener('open', () => {
      this.handlers.onStateChange?.('connected');
      this.handlers.onDataChannelOpen?.();
    });
    dc.addEventListener('close', () => {
      this.handlers.onStateChange?.('done');
      this.handlers.onDataChannelClose?.();
    });
    dc.addEventListener('error', (event) => {
      this.handlers.onError?.(new Error('DataChannel error: ' + (event as unknown as Error).message));
    });
    dc.addEventListener('message', (event) => {
      if (typeof event.data === 'string') {
        const msg = parseDataChannelMessage(event.data);
        if (msg) this.handleMessage(msg);
      } else if (event.data instanceof ArrayBuffer) {
        this.handleBinary(event.data);
      }
    });
  }

  private handleMessage(msg: DataChannelMessage) {
    switch (msg.type) {
      case 'FILE_LIST':
        this.handlers.onFileList?.(msg.files);
        break;
      case 'FILE_LIST_ACK':
        this.selectedIds = msg.selectedIds;
        this.pendingFiles = this.pendingFiles.filter((f) => this.selectedIds.includes(f.id));
        this.currentFileIndex = 0;
        this.startSendingNextFile();
        break;
      case 'DECLINE':
        this.handlers.onDecline?.();
        this.handlers.onStateChange?.('declined');
        this.cleanup();
        break;
      case 'FILE_HEADER':
        // 接收方：开始接收新文件
        this.incomingFileBuffers.set(msg.id, []);
        break;
      case 'FILE_END':
        // 接收方：文件接收完成，组装 blob
        this.finalizeIncomingFile(msg.id);
        break;
      case 'FILE_ACK':
        // 发送方：单个文件 ACK，继续下一个
        this.handlers.onFileAck?.(msg.id, msg.success, msg.error);
        if (msg.success) {
          this.currentFileIndex++;
          this.startSendingNextFile();
        } else {
          this.handlers.onError?.(new Error(`文件 ${msg.id} 传输失败: ${msg.error}`));
        }
        break;
      case 'END':
        this.handlers.onAllDone?.();
        this.handlers.onStateChange?.('done');
        this.cleanup();
        break;
      case 'CANCEL':
        this.handlers.onCancel?.(msg.reason);
        this.handlers.onStateChange?.('canceled');
        this.cleanup();
        break;
    }
  }

  private handleBinary(buffer: ArrayBuffer) {
    // 找到正在接收的文件
    const fileId = this.currentIncomingFileId();
    if (!fileId) return;
    const chunks = this.incomingFileBuffers.get(fileId);
    if (!chunks) return;
    chunks.push(buffer);
    const meta = this.incomingFileMeta.get(fileId);
    if (meta) {
      const received = chunks.reduce((sum, c) => sum + c.byteLength, 0);
      this.handlers.onFileProgress?.(fileId, received, meta.size);
    }
  }

  private currentIncomingFileId(): string | null {
    for (const [id, chunks] of this.incomingFileBuffers) {
      // 简化：取第一个未完成的文件（按 FILE_HEADER 顺序）
      void chunks;
      return id;
    }
    return null;
  }

  private finalizeIncomingFile(fileId: string) {
    const chunks = this.incomingFileBuffers.get(fileId);
    const meta = this.incomingFileMeta.get(fileId);
    if (!chunks || !meta) return;
    const blob = new Blob(chunks, { type: meta.mimeType ?? 'application/octet-stream' });
    this.handlers.onFileReceived?.(fileId, blob, meta.fileName);
    this.send({ type: 'FILE_ACK', id: fileId, success: true });
    this.incomingFileBuffers.delete(fileId);
    this.incomingFileMeta.delete(fileId);
  }

  // ===== 发送方 API =====

  setPendingFiles(files: FileDto[]) {
    this.pendingFiles = files;
  }

  sendFileList() {
    this.send({ type: 'FILE_LIST', files: this.pendingFiles });
  }

  // 接收方：选择接受哪些文件
  acceptFiles(selectedIds: string[]) {
    this.send({ type: 'FILE_LIST_ACK', selectedIds });
  }

  decline() {
    this.send({ type: 'DECLINE' });
    this.handlers.onStateChange?.('declined');
    this.cleanup();
  }

  // 接收方：注册即将接收的文件元数据
  registerIncomingFiles(files: FileDto[]) {
    for (const f of files) {
      this.incomingFileMeta.set(f.id, f);
    }
  }

  cancel(reason?: string) {
    this.send({ type: 'CANCEL', reason });
    this.handlers.onStateChange?.('canceled');
    this.cleanup();
  }

  private async startSendingNextFile() {
    if (this.currentFileIndex >= this.pendingFiles.length) {
      this.send({ type: 'END' });
      this.handlers.onAllDone?.();
      this.handlers.onStateChange?.('done');
      this.cleanup();
      return;
    }
    const file = this.pendingFiles[this.currentFileIndex];
    this.handlers.onStateChange?.('transferring');
    this.send({ type: 'FILE_HEADER', id: file.id });
    await this.streamFile(file);
    this.send({ type: 'FILE_END', id: file.id });
  }

  private async streamFile(file: FileDto): Promise<void> {
    if (!this.dc) return;
    // file.id 关联到 File 对象，通过外部注入的 fileMap
    const blob = this.fileMap.get(file.id);
    if (!blob) {
      this.send({ type: 'FILE_ACK', id: file.id, success: false, error: '文件未找到' });
      return;
    }
    const total = blob.size;
    let offset = 0;
    while (offset < total) {
      const end = Math.min(offset + CHUNK_SIZE, total);
      const slice = blob.slice(offset, end);
      const buf = await slice.arrayBuffer();
      // 背压：等待 bufferedAmount 降到低水位
      while (this.dc.bufferedAmount > BUFFER_HIGH_WATERMARK) {
        await this.waitForBufferDrain();
      }
      this.dc.send(buf);
      offset = end;
      this.handlers.onFileProgress?.(file.id, offset, total);
    }
  }

  private waitForBufferDrain(): Promise<void> {
    return new Promise((resolve) => {
      if (!this.dc) return resolve();
      const onLow = () => {
        this.dc?.removeEventListener('bufferedamountlow', onLow);
        resolve();
      };
      this.dc.addEventListener('bufferedamountlow', onLow);
    });
  }

  private fileMap = new Map<string, Blob>();

  setFileBlob(id: string, blob: Blob) {
    this.fileMap.set(id, blob);
  }

  private send(msg: DataChannelMessage) {
    if (this.dc?.readyState === 'open') {
      this.dc.send(stringifyDataChannelMessage(msg));
    }
  }

  private cleanup() {
    try {
      this.dc?.close();
    } catch {
      /* ignore */
    }
    try {
      this.pc?.close();
    } catch {
      /* ignore */
    }
    this.dc = null;
    this.pc = null;
    this.fileMap.clear();
    this.incomingFileBuffers.clear();
    this.incomingFileMeta.clear();
  }

  dispose() {
    this.cleanup();
  }
}
